/**
 * Selectors for the /admin/app-config definition sync.
 *
 * `selectDefinitionDiff` is where the manifest meets the environment. It is
 * memoised on the live records alone, so re-rendering the page does not re-run
 * the diff, and the page never computes buckets for itself.
 *
 * `selectVeritoneAppId` lives here rather than in the service layer so the card
 * can read the app id without importing `callGraphQLApi` — and with it all of
 * `callApi`'s glc-redux surface — just to print it in a header.
 *
 * Who may use the admin tools is decided here too (VE-30689), at TWO bars,
 * because the two tools do not protect the same thing:
 *
 * - `selectIsAppConfigAccessDenied` — /admin/app-config and every definition
 *   mutation. Superuser only, as REDACT POLICY — see below.
 * - `selectIsAdminAccessDenied` — /admin/dependencies and the side nav. Any
 *   admin right. That page only reports registry health and writes nothing.
 *
 * Both also require the instance root org. Each has a `KnownDenied` twin for
 * the route guards, which run before the user record has loaded.
 *
 * THE SUPERUSER BAR IS OURS, NOT THE PLATFORM'S. Verified against aiWARE and
 * confirmed by manual test: `_validateInputAppConfigDefinitions`
 * (aiware-core `dal/dalApplication.js`) refuses a non-superadmin only at
 * `configLevel: instance`, and Redact's manifest is 33 Organization + 5 User
 * with no instance-level entries. An Organization Admin (`admin.org.update`)
 * of the root org CAN create, update and delete every definition this page
 * manages — tested on stage, it succeeds.
 *
 * Why we are stricter anyway: definition rows are org-scoped
 * (`app_config_definition.organization_guid NOT NULL`) and Redact never sends
 * `orgId`, so a write lands on the caller's org. aiWARE therefore sees an org
 * admin editing their own org and is right to allow it. What aiWARE cannot
 * know is that REDACT TREATS THE ROOT ORG'S DEFINITIONS AS THE ENVIRONMENT
 * DEFAULTS — so in that one org the same write changes the config schema every
 * tenant inherits. Keeping it to superusers is a deliberate narrowing of who
 * can do that, not enforcement of a platform rule.
 */
import { createSelector } from 'reselect';
import { modules } from '@veritone/glc-redux';
import { FALLBACK_VERITONE_APP_ID } from '@helpers/constants';
import { AdminState, DefinitionDiff, RootOrgStatus } from './models';
import { diffDefinitions } from './models/diffDefinitions';
import { CONFIG_DEFINITION_MANIFEST } from './models/manifest';
import { AdminStore, defaultState, namespace } from './store';

const {
  config: { getConfig },
} = modules;

/**
 * glc-redux's user slice key (`modules.user.namespace`). A literal rather than
 * read off `modules` at import time: suites that mock glc-redux with only the
 * `config` module would otherwise fail to load this file at all.
 */
const USER_NAMESPACE = 'user';

/**
 * aiWARE permission ids (glc-redux `helpers/permissions`, which the package
 * does not export): `superadmin`, and `admin.org.update` — an org admin.
 *
 * These are NOT two spellings of the same thing, and the difference is why
 * there are two bars below. `superadmin` is what aiWARE's own docs call a
 * superuser, the account that opens Instance Admin Center and administers
 * every org in the environment. `admin.org.update` is an ORDINARY
 * Organization Admin — any org can have them, and being in the root org does
 * not promote one into the other.
 */
const SUPERADMIN_PERMISSION_ID = 1;
const ORG_ADMIN_PERMISSION_ID = 4;

/** glc-redux's `hasAccessTo`: one bit per permission id, 32 ids per mask. */
const hasPermission = (
  permissionId: number,
  masks: readonly number[]
): boolean =>
  (((masks[Math.floor(permissionId / 32)] ?? 0) >>> permissionId % 32) & 1) ===
  1;

interface UserRecord {
  readonly permissionMasks?: readonly number[];
}

/**
 * The glc-redux user record, or `undefined` before `/v1/admin/current-user`
 * has answered (glc-redux starts it as `{}`).
 *
 * Read by hand rather than through glc-redux's `selectUser`, which throws on a
 * state without the user slice instead of answering "not loaded".
 */
const selectUserRecord = (state: unknown): UserRecord | undefined => {
  const user = (state as { [USER_NAMESPACE]?: { user?: UserRecord } })[
    USER_NAMESPACE
  ]?.user;
  return user && Object.keys(user).length > 0 ? user : undefined;
};

/**
 * The Redact app id this environment is configured with.
 *
 * Lives here rather than in the service layer so the card can read it without
 * importing `callGraphQLApi` — and with it the whole of `callApi`'s glc-redux
 * surface — just to print an id in a header.
 */
export const selectVeritoneAppId = (state: unknown): string => {
  const config = getConfig(state as never) as { veritoneAppId?: string };
  return config?.veritoneAppId || FALLBACK_VERITONE_APP_ID;
};

export const selectStore = (state: object): AdminState =>
  (state as Partial<AdminStore>)[namespace] || defaultState;

export const selectIsScanning = createSelector(
  selectStore,
  (s) => s.isScanning
);

/**
 * True once a scan has succeeded. Consumers must gate on this before reading
 * the diff: before it, "nothing missing" and "we have not looked" are the
 * same empty result.
 */
export const selectHasScanned = createSelector(
  selectStore,
  (s) => s.hasScanned
);

export const selectScanError = createSelector(selectStore, (s) => s.scanError);

export const selectScannedAt = createSelector(selectStore, (s) => s.scannedAt);

export const selectLiveDefinitions = createSelector(
  selectStore,
  (s) => s.liveDefinitions
);

export const selectDefinitionDiff = createSelector(
  selectLiveDefinitions,
  (records): DefinitionDiff =>
    diffDefinitions(CONFIG_DEFINITION_MANIFEST, records)
);

/**
 * True when the definitions read but this organization's values did not, so
 * the page must not claim that every key is taking the environment default.
 */
export const selectValuesUnavailable = createSelector(
  selectStore,
  (s) => s.valuesUnavailable
);

/**
 * The masks of `/v1/admin/current-user`, or `undefined` before it answers.
 *
 * That record is loaded during boot, so it is there by the time any page or
 * route saga runs — but NOT when `onBeforeChange` judges the first route of a
 * page load, which runs before boot. The route guards therefore ask the
 * `KnownDenied` selectors rather than these.
 *
 * Rights FAIL CLOSED, unlike the root-org check. That one is a GraphQL field
 * an older cluster may not expose; this is the user record every session has.
 */
const selectPermissionMasks = (
  state: unknown
): readonly number[] | undefined => {
  const masks = selectUserRecord(state)?.permissionMasks;
  return Array.isArray(masks) ? masks : undefined;
};

/**
 * A superuser — aiWARE's instance admin (VE-30689). Grantable only in the root
 * org, so this bit effectively implies the root org on its own.
 *
 * The bar for the app-config tool. `admin.org.update` deliberately does NOT
 * satisfy it, even though the API would accept one: an Organization Admin of
 * the root org is editing what Redact treats as the environment defaults, and
 * we keep that narrower than the platform does. See the file header.
 */
export const selectIsSuperuser = (state: unknown): boolean => {
  const masks = selectPermissionMasks(state);
  return !!masks && hasPermission(SUPERADMIN_PERMISSION_ID, masks);
};

/**
 * Any administrator — superuser OR Organization Admin.
 *
 * The bar for the read-only dependencies tool, and for showing the nav entry
 * at all. Not sufficient for anything that writes.
 */
export const selectHasAdminRights = (state: unknown): boolean => {
  const masks = selectPermissionMasks(state);
  return (
    !!masks &&
    (hasPermission(SUPERADMIN_PERMISSION_ID, masks) ||
      hasPermission(ORG_ADMIN_PERMISSION_ID, masks))
  );
};

export const selectRootOrgStatus = createSelector(
  selectStore,
  (s): RootOrgStatus => s.rootOrgStatus
);

/**
 * True when this session is DEFINITELY not allowed into the dependencies tool:
 * no admin right at all, or the org is confirmed not to be the root org. An
 * unanswered root-org check (`checking` / `unknown`) does not deny on its own
 * — see `RootOrgStatus`.
 */
export const selectIsAdminAccessDenied = createSelector(
  selectHasAdminRights,
  selectRootOrgStatus,
  (hasRights, rootOrgStatus) => !hasRights || rootOrgStatus === 'notRoot'
);

/**
 * The same, for the app-config tool and every definition mutation: superuser
 * only. Strictly narrower than `selectIsAdminAccessDenied` — anything denied
 * there is denied here too.
 */
export const selectIsAppConfigAccessDenied = createSelector(
  selectIsSuperuser,
  selectRootOrgStatus,
  (isSuperuser, rootOrgStatus) => !isSuperuser || rootOrgStatus === 'notRoot'
);

/**
 * The route guards' versions: the same answer once the user record has loaded,
 * and only the root-org half before then.
 *
 * Before boot there is no user record, so "no rights" would really mean "not
 * asked yet" — and failing closed on that sent every real admin Home on a
 * reload or a deep link to a tool. The pages render only after boot, ask the
 * strict selector, and turn a denied user away themselves; the route saga of
 * /admin/dependencies checks again before it runs anything.
 */
const knownDenied =
  (strict: (state: object) => boolean) =>
  (state: unknown): boolean =>
    selectUserRecord(state)
      ? strict(state as object)
      : selectRootOrgStatus(state as object) === 'notRoot';

export const selectIsAdminAccessKnownDenied = knownDenied(
  selectIsAdminAccessDenied
);

export const selectIsAppConfigAccessKnownDenied = knownDenied(
  selectIsAppConfigAccessDenied
);

export const selectOrganizationName = createSelector(
  selectStore,
  (s) => s.organizationName
);

/**
 * Whether the page offers its mutation controls.
 *
 * Superuser only, and never on a confirmed non-root org. An `unknown` root-org
 * answer still counts as allowed: that half is advisory — the permission model
 * may not be exactly "root org only" everywhere — so an unconfirmed superuser
 * is left to try and to receive the API's own refusal on the row, rather than
 * facing a disabled button that explains nothing.
 */
export const selectCanMutateDefinitions = createSelector(
  selectIsAppConfigAccessDenied,
  (denied) => !denied
);

export const selectPendingDefinitionKeys = createSelector(
  selectStore,
  (s) => s.pendingKeys
);

export const selectDefinitionErrors = createSelector(
  selectStore,
  (s) => s.errorsByKey
);

/** Transient per-key "this just worked", for the row to show in place. */
export const selectDefinitionSuccess = createSelector(
  selectStore,
  (s) => s.successByKey
);
